import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ServiceOption } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { CalendarCheck, Clock, Globe, Mail, Phone } from 'lucide-react'
import { getServiceOptions } from '@/lib/db/queries'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/generals/page-hero'
import { WhatsappIcon } from '@/components/generals/whatsapp-icon'
import { BookingForm } from '@/components/forms/booking-form'
import { cateringProcess } from '@/constants/process'
import { contact, whatsappLink } from '@/constants/site'

interface BookPageProps {
    services: Array<ServiceOption>
}

const BookPage: FC<BookPageProps> = ({ services = [] }): ReactNode => {
    const router = useRouter()
    const requested = typeof router.query.service === 'string' ? router.query.service : ''
    const initialService = services.find(service => service.slug === requested)?.title ?? ''

    return (
        <Layout>
            <Seo
                title='Book catering'
                description='Request a catering quote from Loveth Tasty Global for weddings, corporate events, birthdays, private dinners and celebrations in Akure and across Nigeria.'
            />

            <PageHero
                eyebrow='Book Catering'
                breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Book Catering' }]}
                title={(
                    <>
                        Let&apos;s plan your{' '}
                        <em className='text-brand-burnt'>perfect menu.</em>
                    </>
                )}
                description={(
                    <p>
                        Tell us about your event and we will come back to you with a tailored menu and a clear quote. It only takes a couple of minutes.
                    </p>
                )}
            />

            <section aria-label='Booking form' className='bg-brand-white py-16 lg:py-24'>
                <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-6 lg:grid-cols-12 lg:px-10'>
                    <div className='lg:col-span-8'>
                        <div className='rounded-[2rem] border border-brand-sand bg-brand-white p-6 shadow-[0_50px_100px_-60px_rgba(32,32,32,0.5)] sm:p-10'>
                            <BookingForm key={initialService} services={services} initialService={initialService} />
                        </div>
                    </div>

                    <aside className='space-y-5 lg:col-span-4'>
                        <div className='rounded-[1.75rem] bg-brand-charcoal p-7 text-brand-white'>
                            <h2 className='flex items-center gap-2 font-heading text-2xl font-semibold'>
                                <CalendarCheck className='size-5 text-brand-gold' aria-hidden='true' />
                                What happens next
                            </h2>

                            <ol className='mt-6 space-y-5'>
                                {cateringProcess.map((step, index) => (
                                    <li key={step.title} className='flex gap-4'>
                                        <span aria-hidden='true' className='inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-brand-gold/50 font-heading text-base font-semibold text-brand-gold'>
                                            {index + 1}
                                        </span>

                                        <div>
                                            <h3 className='font-semibold'>
                                                {step.title}
                                            </h3>

                                            <p className='mt-1 text-sm leading-relaxed text-brand-mist'>
                                                {step.text}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>

                        <div className='rounded-[1.75rem] border border-brand-sand bg-brand-cream p-7'>
                            <h2 className='font-heading text-2xl font-semibold text-brand-charcoal'>
                                Prefer to talk?
                            </h2>

                            <p className='mt-2 flex items-center gap-2 text-sm text-brand-stone'>
                                <Clock className='size-4 text-brand-burnt' aria-hidden='true' />
                                For urgent dates, a quick chat is fastest.
                            </p>

                            <ul className='mt-5 space-y-3 text-[15px]'>
                                <li>
                                    <a
                                        href={whatsappLink("Hello Loveth Tasty, I'd like to book catering for an event.")}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='inline-flex items-center gap-3 font-semibold text-brand-charcoal hover:text-brand-ember'
                                    >
                                        <WhatsappIcon className='size-4 text-brand-burnt' />
                                        Chat on WhatsApp
                                    </a>
                                </li>

                                <li>
                                    <a href={contact.phoneHref} className='inline-flex items-center gap-3 font-semibold text-brand-charcoal hover:text-brand-ember'>
                                        <Phone className='size-4 text-brand-burnt' aria-hidden='true' />
                                        {contact.phoneDisplay}
                                    </a>
                                </li>

                                <li>
                                    <a href={`mailto:${contact.email}`} className='inline-flex items-center gap-3 font-semibold break-all text-brand-charcoal hover:text-brand-ember'>
                                        <Mail className='size-4 shrink-0 text-brand-burnt' aria-hidden='true' />
                                        {contact.email}
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <Link
                            href='/order-from-abroad'
                            className='group flex items-center gap-4 rounded-[1.75rem] border border-brand-sand p-6 transition-colors hover:border-brand-gold/60 hover:bg-brand-cream/60'
                        >
                            <span className='inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-orange/15 text-brand-burnt'>
                                <Globe className='size-5' aria-hidden='true' />
                            </span>

                            <span>
                                <span className='block font-semibold text-brand-charcoal'>
                                    Ordering from abroad?
                                </span>

                                <span className='block text-sm text-brand-stone'>
                                    Send food to family in Nigeria and pay in NGN, USD or GBP.
                                </span>
                            </span>
                        </Link>
                    </aside>
                </div>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<BookPageProps> = async () => {
    try {
        return { props: { services: await getServiceOptions() }, revalidate: 60 }
    }
    catch (error) {
        console.error('Booking page could not load services:', error instanceof Error ? error.message : error)

        return { props: { services: [] }, revalidate: 10 }
    }
}

export default BookPage
